import { DASH, formatInt, formatPct, funnelGeometry, platformLabel } from './marketingMath';

export interface FunnelBarStep {
  key: string;
  label: string;
  count: number | null;
  /** Ratio to the step before (0.178 → "17.8%"); null for the first step or when either side is unknown. */
  rateFromPrevious: number | null;
  byPlatform?: Record<string, number | null> | null;
  /** Why the count is missing (shown as a tooltip on the "—"). */
  nullReason?: string;
}

interface Props {
  title: string;
  steps: FunnelBarStep[];
  testId: string;
}

const PLATFORMS = ['ios', 'android', 'web'];

function splitText(by: Record<string, number | null> | null | undefined): string | null {
  if (!by) return null;
  return PLATFORMS.filter((p) => p in by).map((p) => `${platformLabel(p)} ${by[p] == null ? DASH : formatInt(by[p])}`).join(' · ');
}

/** A step can exceed the one before it (store taps include organic traffic): show words instead of "7,460.1%". */
const ABOVE_PREVIOUS = 'above previous step';
const ABOVE_PREVIOUS_HINT = 'This step counts organic traffic too, so it can exceed the step before it';

function isAbovePrevious(s: FunnelBarStep): boolean {
  return s.rateFromPrevious != null && s.rateFromPrevious > 1;
}

/** `visible` = what is drawn; the accessible summary keeps the exact percentage. */
function valueText(s: FunnelBarStep, first: boolean, visible = false): string {
  if (s.count == null) return DASH;
  if (first || s.rateFromPrevious == null) return formatInt(s.count);
  const rate = visible && isAbovePrevious(s) ? ABOVE_PREVIOUS : `${formatPct(s.rateFromPrevious)} of previous`;
  return `${formatInt(s.count)} · ${rate}`;
}

// SVG text cannot wrap or ellipsize, so the columns are sized from these generous per-character widths (px at the
// 13px / 11px sizes used in marketing.css, tabular figures).
const LABEL_PX = 7.8;
const SPLIT_PX = 6.4;
const VALUE_PX = 7.6;

/**
 * Spec 084 — a labelled funnel: one horizontal bar per step, the step name as text on the left and the count with
 * the % of the previous step on the right, so nothing depends on colour. Hand-made SVG (as DailyTapsChart) plus a
 * visually hidden table with the same numbers and the platform split.
 */
export default function FunnelBar({ title, steps, testId }: Props) {
  const g = funnelGeometry(steps.map((s) => s.count), {
    labelPx: Math.max(0, ...steps.map((s) => Math.max(s.label.length * LABEL_PX, (splitText(s.byPlatform)?.length ?? 0) * SPLIT_PX))),
    valuePx: Math.max(0, ...steps.map((s, i) => valueText(s, i === 0, true).length * VALUE_PX)),
  });
  const anySplit = steps.some((s) => s.byPlatform);
  const summary = `${title}: ${steps.map((s, i) => `${s.label} ${valueText(s, i === 0)}`).join('; ')}.`;
  return (
    <figure className="mkt-chart mkt-funnel" data-testid={testId}>
      <figcaption className="mkt-chart__head"><span className="mkt-chart__title">{title}</span></figcaption>
      <svg viewBox={`0 0 ${g.width} ${g.height}`} role="img" aria-label={summary} className="mkt-funnel__svg">
        {steps.map((s, i) => {
          const r = g.rows[i];
          const split = splitText(s.byPlatform);
          return (
            <g key={s.key} data-step={s.key}>
              {s.count == null && s.nullReason && <title>{`${s.label}: ${s.nullReason}`}</title>}
              {s.count != null && isAbovePrevious(s) && <title>{ABOVE_PREVIOUS_HINT}</title>}
              <text className="mkt-funnel__label" x={0} y={r.labelY - (split ? 6 : 0)}>{s.label}</text>
              {split && <text className="mkt-funnel__split" x={0} y={r.labelY + 9}>{split}</text>}
              <rect className="mkt-funnel__track" x={g.labelW} y={r.y} width={g.barMax} height={g.rowH - 12} rx={4} />
              {r.width > 0 && <rect className={`mkt-funnel__bar mkt-funnel__bar--${i % 2 ? 'b' : 'a'}`} x={g.labelW} y={r.y} width={r.width} height={g.rowH - 12} rx={4} />}
              <text className="mkt-funnel__value" x={g.labelW + g.barMax + 10} y={r.labelY} data-testid={`${testId}-value-${s.key}`}>
                {valueText(s, i === 0, true)}
              </text>
            </g>
          );
        })}
      </svg>
      <table className="sr-only" data-testid={`${testId}-table`}>
        <caption>{title}</caption>
        <thead>
          <tr>
            <th scope="col">Step</th><th scope="col">Count</th><th scope="col">% of previous</th>
            {anySplit && PLATFORMS.map((p) => <th key={p} scope="col">{platformLabel(p)}</th>)}
          </tr>
        </thead>
        <tbody>
          {steps.map((s, i) => (
            <tr key={s.key}>
              <th scope="row">{s.label}</th>
              <td title={s.count == null ? s.nullReason : undefined}>{s.count == null ? DASH : formatInt(s.count)}</td>
              <td>{i === 0 ? DASH : formatPct(s.rateFromPrevious)}</td>
              {anySplit && PLATFORMS.map((p) => <td key={p}>{s.byPlatform && s.byPlatform[p] != null ? formatInt(s.byPlatform[p]) : DASH}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}
